import React, { useCallback, useEffect, useRef, useState } from 'react';

import Input from 'components/partials/input/input';

import {
  formatPercentInputValue,
  normalizePercentRaw,
} from '../utils/percent-field-utils';

/** Same idea as the amount field’s `replace(/[^0-9]/g)` — then {@link normalizePercentRaw}. */
const stripToPercentInputChars = (value: unknown): string =>
  String(value).replace(/[^0-9.-]/g, '');

interface CoaPercentChangeInputProps {
  pctValue: number | string | null;
  disabled?: boolean;
  coaId: string | number;
  expenseIndex: number;
  expenseLabel?: string;
  onPercentChange: (
    coaId: string | number,
    expenseIndex: number,
    value: number
  ) => void;
  onAmountChange: (
    coaId: string | number,
    expenseIndex: number,
    value: number | null
  ) => void;
}

export const CoaPercentChangeInput = ({
  pctValue,
  disabled,
  coaId,
  expenseIndex,
  expenseLabel,
  onPercentChange,
  onAmountChange,
}: CoaPercentChangeInputProps): React.JSX.Element => {
  const editingCanonicalRef = useRef<string | undefined>(undefined);
  const [useDashPlaceholder, setUseDashPlaceholder] = useState<boolean>(true);

  useEffect(() => {
    if (pctValue !== null && pctValue !== '') {
      setUseDashPlaceholder(false);
    }
  }, [pctValue]);

  const displayValue =
    editingCanonicalRef.current !== undefined
      ? formatPercentInputValue(editingCanonicalRef.current as never)
      : formatPercentInputValue(pctValue as never);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = stripToPercentInputChars(e.target.value);
      const canonical = normalizePercentRaw(raw);

      editingCanonicalRef.current = canonical as unknown as string;

      const parsed = Number.parseFloat(canonical as unknown as string);

      if (!Number.isNaN(parsed)) {
        onPercentChange(coaId, expenseIndex, parsed);
      } else if (canonical === '' || canonical === '-') {
        onAmountChange(coaId, expenseIndex, null);
      }
    },
    [coaId, expenseIndex, onAmountChange, onPercentChange]
  );

  const handleFocus = useCallback(() => {
    const canonical = normalizePercentRaw(pctValue as never);

    editingCanonicalRef.current = canonical as unknown as string;
  }, [pctValue]);

  const handleBlur = useCallback(() => {
    const canonical = editingCanonicalRef.current;

    editingCanonicalRef.current = undefined;

    if (canonical === undefined) {
      return;
    }

    const parsed = Number.parseFloat(canonical);

    if (Number.isNaN(parsed)) {
      onPercentChange(coaId, expenseIndex, 0);
    }
  }, [coaId, expenseIndex, onPercentChange]);

  return (
    <Input
      type="text"
      value={displayValue as never}
      disabled={disabled}
      onFocus={handleFocus as never}
      onBlur={handleBlur as never}
      onChange={handleChange as never}
      endAdornment="%"
      placeholder={useDashPlaceholder ? '-' : ''}
      inputCustomClass="h-7 !py-1 text-sm text-left"
      inputWrapperCustomClass="!m-0 min-w-[108px] w-[108px]"
      aria-label={`Percent of change for ${expenseLabel ?? 'expense'}`}
      data-testid={`coa-percent-${coaId}-${expenseIndex}`}
    />
  );
};
